import { useCallback, useState } from "react";
import useChatApiService from "../services/chat.service";
import { Citation } from "@/constants/citation.constant";
import { loadPolicyLinkMap, resolvePolicyLink } from "@/utils/policyLink";

/**
 * Custom hook for managing citation functionality including individual citation previews and sources modal.
 * 
 * Features:
 * - Individual citation preview with presigned URL generation
 * - Sources modal state management
 * - Loading and error state handling
 * - Separate state for sources modal vs individual citation preview
 * 
 * @returns Object containing citation state and methods
 * @returns {boolean} open - Whether individual citation modal is open
 * @returns {Citation|null} reference - Current citation being previewed
 * @returns {boolean} isLoading - Loading state for citation operations
 * @returns {string|null} error - Error message if any
 * @returns {Function} openCitation - Function to open individual citation preview
 * @returns {Function} close - Function to close individual citation modal
 * @returns {boolean} sourcesOpen - Whether sources modal is open
 * @returns {Citation[]} sources - Array of sources in the modal
 * @returns {Function} openSources - Function to open sources modal
 * @returns {Function} closeSources - Function to close sources modal
 */
export function useCitation() {
  const { getPresignedUrl } = useChatApiService();
  const [reference, setReference] = useState<Citation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCitationId, setSelectedCitationId] = useState<number | null>(null);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sources, setSources] = useState<Citation[]>([]);
  
  // Citation details modal state
  const [citationDetailsOpen, setCitationDetailsOpen] = useState(false);
  const [citationDetails, setCitationDetails] = useState<Citation | null>(null);

  /**
   * Opens an individual citation preview modal with presigned URL
   * @param citation - The citation to preview
   */
  const openCitation = useCallback(
    async (citation: Citation) => {
      setSelectedCitationId(citation.id)

      setError(null);
      setIsLoading(true);
      try {
        const rawPath = citation.source_file.trim();
        const match = rawPath.match(/^(?:[^/]+)\/(.+)$/);
        const normalizedPath = match ? match[1] : rawPath;
        const resp = await getPresignedUrl("policy", { blob_path: normalizedPath, expiry_hours: 2, permission: 'r' }, false);
        const updated = { ...citation, url: resp?.presigned_url || citation.url };
        setReference(updated);
        setOpen(true);
      } catch (err: any) {
        setError(err?.message || "Failed to load citation");
      } finally {
        setIsLoading(false);
      }
    },
    [getPresignedUrl]
  );

  /**
   * Opens the sources modal with a list of citations
   * @param citationSources - Array of citations to display
   */
  const openSources = useCallback((citationSources: Citation[]) => {
    setSources(citationSources);
    setSourcesOpen(true);
  }, []);

  /**
   * Closes the individual citation preview modal
   */
  const close = useCallback(() => {
    setOpen(false);
    setReference(null);
    setError(null);
  }, []);

  /**
   * Closes the sources modal
   */
  const closeSources = useCallback(() => {
    setSourcesOpen(false);
    setSources([]);
  }, []);

  /**
   * Opens the citation details modal with presigned URL
   * @param citation - The citation to show details for
   */
  const openCitationDetails = useCallback(
    async (citation: Citation) => {
      setSelectedCitationId(citation.id);
      setError(null);
      setIsLoading(true);
      try {
        const policyMap = await loadPolicyLinkMap();
        const policyLink = resolvePolicyLink(policyMap, citation);
        if (policyLink) {
          window.open(policyLink, "_blank", "noopener,noreferrer");
          return;
        }
        const rawPath = citation.file_path?.trim() || '';
        const resp = await getPresignedUrl("policy", { blob_path: rawPath, expiry_hours: 2, permission: 'r' }, false);
        const updated = {
          ...citation,
          url: resp?.presigned_url || citation.url
        };
        setCitationDetails(updated);
        setCitationDetailsOpen(true);
      } catch (err: any) {
        setError(err?.message || "Failed to load citation");
      } finally {
        setIsLoading(false);
      }
    },
    [getPresignedUrl]
  );

  /**
   * Closes the citation details modal
   */
  const closeCitationDetails = useCallback(() => {
    setSelectedCitationId(null)
    setCitationDetailsOpen(false);
    setCitationDetails(null);
  }, []);

  return {
    open,
    reference,
    isLoading,
    error,
    openCitation,
    close,
    sourcesOpen,
    sources,
    openSources,
    closeSources,
    citationDetailsOpen,
    citationDetails,
    openCitationDetails,
    closeCitationDetails,
    selectedCitationId
  };
}